import React, { useState } from "react";
import { useNavigate, Navigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { api, API, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft } from "@phosphor-icons/react";

const TABS = [
  { key: "login", tKey: "auth.login_tab" },
  { key: "register", tKey: "auth.register_tab" },
  { key: "forgot", tKey: "auth.forgot_tab" },
];

export default function Login() {
  const { user, login } = useAuth();
  const { t, lang } = useI18n();
  const [params] = useSearchParams();
  const initialTab = params.get("tab") || "login";
  const [tab, setTab] = useState(TABS.find((item) => item.key === initialTab) ? initialTab : "login");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  if (user && user !== false) return <Navigate to="/" replace />;

  const signInWithGoogle = () => {
    window.location.href = `${API}/auth/google/login`;
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      <div className="hidden lg:flex flex-1 relative bg-[#0F172A] overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1684695749267-233af13276d0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHw0fHx3YXJlaG91c2UlMjBsb2dpc3RpY3MlMjBwYWxsZXRzfGVufDB8fHx8MTc4NTcwMTY4MXww&ixlib=rb-4.1.0&q=85"
          alt="Warehouse"
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          <button type="button" className="flex items-center w-fit cursor-pointer" onClick={() => nav("/")}>
            <img
              src="/brand/palletnext-logo.png"
              alt="PalletNext H3POE"
              className="w-[220px] max-h-20 object-contain object-left"
            />
          </button>
          <div>
            <h1 className="font-display font-extrabold text-5xl leading-tight max-w-md">
              Palet planı yerine <span className="text-[#FACC15]">mühendislik.</span>
            </h1>
            <p className="mt-4 text-slate-300 max-w-md">
              Ürünlerinizin ihracatı için en az palet, en yüksek doluluk ve gerçek fiziksel
              yerleşim doğruluğu.
            </p>
          </div>
        </div>
      </div>

      <div className="w-full lg:w-[480px] bg-white flex items-center justify-center px-6 py-12 relative">
        <button
          onClick={() => nav("/")}
          data-testid="back-to-landing"
          className="absolute top-4 left-4 inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-[#2563EB]"
        >
          <ArrowLeft size={14} weight="bold" /> {t("landing.nav.home")}
        </button>
        <div className="w-full max-w-sm">
          <div className="flex border-b border-slate-200 mb-6" data-testid="login-tabs">
            {TABS.map((item) => (
              <button
                key={item.key}
                data-testid={`tab-${item.key}`}
                onClick={() => setTab(item.key)}
                className={`flex-1 pb-2.5 text-xs font-semibold uppercase tracking-wider border-b-2 -mb-px transition-colors ${
                  tab === item.key ? "border-[#2563EB] text-[#2563EB]" : "border-transparent text-slate-400 hover:text-slate-600"
                }`}
              >
                {t(item.tKey)}
              </button>
            ))}
          </div>

          {tab === "login" && (
            <LoginForm
              login={login}
              loading={loading}
              setLoading={setLoading}
              nav={nav}
              t={t}
              lang={lang}
              signInWithGoogle={signInWithGoogle}
              onForgot={() => setTab("forgot")}
            />
          )}
          {tab === "register" && (
            <RegisterForm
              login={login}
              loading={loading}
              setLoading={setLoading}
              nav={nav}
              lang={lang}
              signInWithGoogle={signInWithGoogle}
            />
          )}
          {tab === "forgot" && (
            <ForgotForm loading={loading} setLoading={setLoading} lang={lang} onBack={() => setTab("login")} />
          )}
        </div>
      </div>
    </div>
  );
}

function LoginForm({ login, loading, setLoading, nav, t, lang, signInWithGoogle, onForgot }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success(t("final.login_success"));
      nav("/");
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message;
      setErr(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="login-form">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-slate-500">{t("login.title")}</div>
        <h2 className="font-display text-3xl font-bold mt-1">{t("dashboard.title")}</h2>
      </div>
      <div className="space-y-4">
        <Field label={t("login.email")}>
          <input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="input" required />
        </Field>
        <Field label={t("login.password")}>
          <input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="input" required />
        </Field>
        <div className="text-right">
          <button type="button" onClick={onForgot} data-testid="btn-forgot-link" className="text-[11px] font-semibold text-[#2563EB] hover:underline">
            Şifremi Unuttum?
          </button>
        </div>
      </div>
      {err && <div className="text-sm text-red-600 border-l-2 border-red-500 pl-3 py-1" data-testid="login-error">{err}</div>}
      <button data-testid="login-submit" type="submit" disabled={loading} className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white py-2.5 font-semibold rounded-sm disabled:opacity-60">
        {loading ? `${t("login.submit")}…` : t("login.submit")}
      </button>
      <Divider label={lang === "en" ? "or" : "veya"} />
      <GoogleButton onClick={signInWithGoogle} label={lang === "en" ? "Sign in with Google" : "Google ile Giriş Yap"} testid="btn-google-signin" />
    </form>
  );
}

function RegisterForm({ login, loading, setLoading, nav, lang, signInWithGoogle }) {
  const { t } = useI18n();
  const [f, setF] = useState({ name: "", email: "", password: "", passwordConfirm: "" });
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (f.password.length < 10 || !/[A-Za-zÇĞİÖŞÜçğıöşü]/.test(f.password) || !/\d/.test(f.password)) return setErr(t("final.login_min"));
    if (f.password !== f.passwordConfirm) return setErr(t("final.login_mismatch"));
    setLoading(true);
    try {
      await api.post("/auth/register", { email: f.email, password: f.password, name: f.name });
      await login(f.email, f.password);
      toast.success(t("auth.register_success"));
      nav("/");
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message;
      setErr(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="register-form">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-slate-500">{t("auth.new_account")}</div>
        <h2 className="font-display text-3xl font-bold mt-1">{t("auth.free_try")}</h2>
      </div>
      <Field label={t("login.name")}>
        <input data-testid="register-name" type="text" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="input" required />
      </Field>
      <Field label={t("login.email")}>
        <input data-testid="register-email" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="input" required />
      </Field>
      <Field label={t("auth.password")}>
        <input data-testid="register-password" type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="input" required minLength={10} />
      </Field>
      <Field label={t("auth.password_again")}>
        <input data-testid="register-password-confirm" type="password" value={f.passwordConfirm} onChange={(e) => setF({ ...f, passwordConfirm: e.target.value })} className="input" required minLength={10} />
      </Field>
      {err && <div className="text-sm text-red-600 border-l-2 border-red-500 pl-3 py-1" data-testid="register-error">{err}</div>}
      <button data-testid="register-submit" type="submit" disabled={loading} className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white py-2.5 font-semibold rounded-sm disabled:opacity-60">
        {loading ? t("auth.creating") : t("auth.create_account")}
      </button>
      <Divider label={lang === "en" ? "or" : "veya"} />
      <GoogleButton onClick={signInWithGoogle} label={lang === "en" ? "Sign up with Google" : "Google ile Kayıt Ol"} testid="btn-google-signup" />
      <div className="text-[11px] text-slate-500 text-center">{t("auth.terms")}</div>
    </form>
  );
}

function ForgotForm({ loading, setLoading, onBack }) {
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/password-reset/request", { email });
      setSent(true);
      toast.success("Şifre sıfırlama bağlantısı e-posta adresinize gönderildi.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-slate-500">{t("auth.recovery")}</div>
        <h2 className="font-display text-3xl font-bold mt-1">{t("auth.forgot_tab")}</h2>
        <p className="text-sm text-slate-500 mt-2">Kayıtlı e-posta adresinizi girin. Hesabınız mevcutsa 30 dakika geçerli şifre sıfırlama bağlantısı gönderilecektir.</p>
      </div>
      <Field label={t("login.email")}>
        <input data-testid="forgot-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="input" required />
      </Field>
      {sent && (
        <div className="text-sm bg-emerald-50 border-l-2 border-emerald-500 text-emerald-800 p-3" data-testid="forgot-success">
          Hesabınız mevcutsa şifre sıfırlama bağlantısı kayıtlı e-posta adresinize gönderildi. Gelen kutusu ve spam klasörünü kontrol edin.
        </div>
      )}
      <button data-testid="forgot-submit" type="submit" disabled={loading || sent} className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white py-2.5 font-semibold rounded-sm disabled:opacity-60">
        {loading ? t("auth.sending") : sent ? "Bağlantı Gönderildi" : "Şifre Sıfırlama Linki Gönder"}
      </button>
      <button type="button" onClick={onBack} className="w-full text-xs text-slate-500 hover:text-[#2563EB] font-semibold">
        {t("auth.back_login")}
      </button>
    </form>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs font-semibold text-slate-600 uppercase tracking-wider">{label}</div>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function Divider({ label }) {
  return (
    <div className="relative py-1">
      <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200" /></div>
      <div className="relative flex justify-center text-[10px] uppercase tracking-widest">
        <span className="bg-white px-3 text-slate-400">{label}</span>
      </div>
    </div>
  );
}

function GoogleButton({ onClick, label, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className="w-full inline-flex items-center justify-center gap-3 border border-slate-300 hover:border-slate-400 hover:bg-slate-50 text-slate-800 py-2.5 font-semibold rounded-sm transition-colors"
    >
      <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
        <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
        <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
        <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
        <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
      </svg>
      <span>{label}</span>
    </button>
  );
}